import React, { useState } from 'react'
import { theme } from '../../styles/theme'
import { TrendingUp, TrendingDown, DollarSign, Globe, Package, Target } from 'lucide-react'
import { SparklineChart } from '../common/Chart'
import { Card } from '../common/Card'

export interface MetricData {
  title: string
  value: string | number
  trend?: number
  trendDirection?: 'up' | 'down'
  icon: 'revenue' | 'markets' | 'products' | 'score'
  sparklineData?: number[]
  color: string
}

interface MetricsCardProps {
  metric: MetricData
  onClick?: () => void
}

const iconMap = {
  revenue: DollarSign,
  markets: Globe,
  products: Package,
  score: Target,
}

export const MetricsCard: React.FC<MetricsCardProps> = ({ metric, onClick }) => {
  const [isHovered, setIsHovered] = useState(false)
  const Icon = iconMap[metric.icon]

  const trendColor = metric.trendDirection === 'up' ? theme.colors.success : theme.colors.error
  const TrendIcon = metric.trendDirection === 'up' ? TrendingUp : TrendingDown

  return (
    <Card
      style={{
        cursor: onClick ? 'pointer' : 'default',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        transition: theme.transitions.base,
        border: `2px solid ${isHovered ? metric.color : 'transparent'}`,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: theme.spacing[4] }}>
        <div
          style={{
            width: '48px',
            height: '48px',
            borderRadius: theme.borderRadius.lg,
            backgroundColor: `${metric.color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: theme.spacing[3],
          }}
        >
          <Icon size={24} color={metric.color} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: theme.typography.fontSize.sm,
            color: theme.colors.textSecondary,
            marginBottom: theme.spacing[1],
          }}>
            {metric.title}
          </div>
          <div style={{
            fontSize: theme.typography.fontSize['3xl'],
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.textPrimary,
          }}>
            {metric.value}
          </div>
        </div>
      </div>

      {metric.trend !== undefined && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing[2],
          marginBottom: theme.spacing[3],
        }}>
          <TrendIcon size={16} color={trendColor} />
          <span style={{
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium,
            color: trendColor,
          }}>
            {metric.trend > 0 ? '+' : ''}{metric.trend}%
          </span>
          <span style={{
            fontSize: theme.typography.fontSize.sm,
            color: theme.colors.textSecondary,
          }}>
            vs last quarter
          </span>
        </div>
      )}

      {metric.sparklineData && metric.sparklineData.length > 0 && (
        <div style={{ marginTop: theme.spacing[3] }}>
          <SparklineChart data={metric.sparklineData} color={metric.color} />
        </div>
      )}
    </Card>
  )
}

interface MetricsRowProps {
  metrics?: MetricData[]
  loading?: boolean
}

export const MetricsRow: React.FC<MetricsRowProps> = ({ metrics, loading }) => {
  const defaultMetrics: MetricData[] = [
    {
      title: 'Revenue Growth',
      value: '+23%',
      trend: 15.2,
      trendDirection: 'up',
      icon: 'revenue',
      sparklineData: [20, 25, 22, 28, 30, 27, 35, 33, 38, 40],
      color: theme.colors.primary,
    },
    {
      title: 'Active Markets',
      value: 12,
      trend: 8.5,
      trendDirection: 'up',
      icon: 'markets',
      sparklineData: [8, 8, 9, 10, 10, 11, 11, 12, 12, 12],
      color: theme.colors.success,
    },
    {
      title: 'Product Performance',
      value: '87%',
      trend: -2.1,
      trendDirection: 'down',
      icon: 'products',
      sparklineData: [90, 89, 91, 88, 87, 89, 88, 86, 87, 87],
      color: theme.colors.warning,
    },
    {
      title: 'Expansion Score',
      value: '8.5/10',
      trend: 5.8,
      trendDirection: 'up',
      icon: 'score',
      sparklineData: [7.5, 7.8, 8.0, 8.1, 8.2, 8.3, 8.4, 8.3, 8.5, 8.5],
      color: theme.colors.purple,
    },
  ]

  const metricsToShow = metrics || defaultMetrics

  if (loading) {
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: theme.spacing[6],
        marginBottom: theme.spacing[8],
      }}>
        {[1, 2, 3, 4].map(i => (
          <Card key={i}>
            <div style={{
              height: '150px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.colors.textSecondary,
            }}>
              Loading...
            </div>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: theme.spacing[6],
      marginBottom: theme.spacing[8],
    }}>
      {metricsToShow.map((metric, index) => (
        <MetricsCard key={index} metric={metric} />
      ))}
    </div>
  )
}
